import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { CellType, IColumnMovingEventArgs, IGridState, IgxDialogComponent, IgxGridComponent, IgxGridStateDirective } from '@infragistics/igniteui-angular';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { UserService } from 'src/app/authentication/user.service';
import { Role } from 'src/app/models/enums/roles';
import { GridState } from 'src/app/models/grid-state';
import { User, UserForm } from 'src/app/models/user';
import { MessagesService } from 'src/app/services/messages.service';
import { restoreState } from 'src/app/shared/utils';

@Component({
    selector: 'app-edit-profiles',
    templateUrl: './edit-profiles.component.html',
    styleUrls: ['./edit-profiles.component.scss']
})
export class EditProfilesComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('grid', { static: true, read: IgxGridComponent })
    private grid!: IgxGridComponent;

    @ViewChild(IgxGridStateDirective, { static: true })
    private state!: IgxGridStateDirective;

    private _editUser?: User;
    private destroy$: Subject<void> = new Subject<void>();
    private gridName: string = 'edit-profiles-grid';
    private gridState?: GridState;

    public users: User[] = [];
    public roles: { name: string, value: number }[] = Object.values(Role).filter(r => isNaN(+r)).map(r => {
        switch (r) {
            case "admin":
                return { name: "Администратор", value: 0 };
            case "moderator":
                return { name: "Модератор", value: 1 };
            default:
                return { name: "Потребител", value: 2 };
        }
    });
    public dialogText: string = 'Добави';
    public userForm: FormGroup<UserForm>;
    public customOverlaySettings = {};
    public updatePasswordUser?: User;

    constructor(
        private msgs: MessagesService,
        private userService: UserService,
        router: Router) {
        this.userForm = new FormGroup<UserForm>({
            username: new FormControl('', Validators.required),
            firstName: new FormControl(''),
            lastName: new FormControl(''),
            email: new FormControl(''),
            role: new FormControl(),
            isActive: new FormControl()
        });
        this.userService.all().then(v => this.users = v);
        router.events.pipe(takeUntil(this.destroy$)).subscribe(e => {
            if (e instanceof NavigationStart) {
                if (this.gridState) {
                    this.gridState.options = this.state.getState().toString();
                    this.gridState.userId = this.userService.currentUser!.id!;
                } else {
                    this.gridState = {
                        gridName: this.gridName,
                        options: this.state.getState().toString(),
                        userId: this.userService.currentUser!.id!,
                    };
                }
                this.userService.updateGridState(this.gridState);
            }
        });
    }

    ngOnInit(): void {
        this.userService.gridStates().pipe(takeUntil(this.destroy$)).subscribe((states: GridState[]) => {
            const loaded = states.filter(s => s.gridName === this.gridName && s.userId === this.userService.currentUser!.id!)[0];
            if (loaded) {
                this.gridState = loaded;
                const gridState: IGridState = JSON.parse(this.gridState?.options ?? '');
                restoreState(gridState, this.grid);
            }
        });
    }

    ngAfterViewInit(): void {
        this.customOverlaySettings = { outlet: this.grid?.outlet };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public parseRole = (cell: CellType): string => {
        switch (cell.value) {
            case 0:
                return "Администратор";
            case 1:
                return "Модератор";
            default:
                return "Потребител";
        }
    }

    public openUserDialog = (dialog: IgxDialogComponent, cell?: CellType): void => {
        this._editUser = cell?.row?.data;
        this.userForm.reset(this._editUser);
        this.dialogText = cell ? 'Редактирай' : 'Добави';
        dialog.open();
    }

    public closeDialog = (dialog: IgxDialogComponent): void => {
        this._editUser = undefined;
        this.userForm.reset();
        dialog.close();
    }

    public userSubmit = (dialog: IgxDialogComponent): void => {
        const add = this.dialogText !== 'Редактирай';
        this._saveUser(dialog, add);
    }

    protected columnMoving = (e: IColumnMovingEventArgs): void => {
        e.cancel = e.source.pinned;
    }

    public openUpdatePasswordDialog = (dialog: IgxDialogComponent, cell: CellType): void => {
        this.updatePasswordUser = cell.row.data;
        dialog.open();
    }

    public updatePassword = async (dialog: IgxDialogComponent): Promise<void> => {
        if (this.updatePasswordUser) {
            try {
                await firstValueFrom(this.userService.renewPassword(this.updatePasswordUser.id));
                this.msgs.success(`На потребител ${this.updatePasswordUser.username} е изпратена нова парола.`);
            } catch (error) {
                this.msgs.error(`Паролата не е променена.`);
            } finally {
                dialog.close();
            }
        }
    }

    private _saveUser = async (dialog: IgxDialogComponent, add: boolean) => {
        try {
            let action = 'редактиран';
            let user: User;
            if (add) {
                user = await this.userService.add(this.userForm.value as User);
                action = 'добавен';
                this.users.push(user);
            } else {
                user = await this.userService.update(Object.assign(this.userForm.value, { id: this._editUser?.id }) as User);
                const index = this.users.findIndex(u => u.id === user.id);
                this.users[index] = user;
            }
            this.users = [... this.users];
            this.msgs.success(`Потребител ${user.username} е успешно ${action}.`);
            this._editUser = undefined;
            this.userForm.reset();
            dialog.close();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
}
